import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';

const ArticlePage = () => {
  const { id } = useParams();
  const [article, setArticle] = useState(null);
  const [articlesAdditionals, setArticlesAdditionals] = useState([]);

  useEffect(() => {
    const fetchArticle = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/furnitures-exterieur/${id}?populate=*`, {
          headers: {
            Authorization: `${process.env.REACT_APP_API_TOKEN}`
          }
        });
        setArticle(response.data.data);
      } catch (error) {
        console.error("Error fetching article:", error);
      }
    };

    const fetchArticlesAdditionals = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/furnitures-exterieur?populate=*`, {
          headers: {
            Authorization: `Bearer 38a0003de7e5dd37d1d078334441e9daa663f6a0eaa40b36276a7032827baf1b9ec5b24fe68de49517bc0026685a3dc9c82663a04c3ee8e84bd175822d27521559ade62e938595e4122a1e9da7ab984114e148b7d72ef808e355814889cb6c79c4b666649e178882249d087778720313a1fa8422452218c7fb5e064161344712`
          }
        });
        setArticlesAdditionals(response.data.data);
      } catch (error) {
        console.error("Error fetching additional images:", error);
      }
    };

    fetchArticle();
    fetchArticlesAdditionals();
  }, [id]);

  if (!article) return <p>Loading...</p>;
  if (article.error) return <p>Error: {article.error.message}</p>;
  if (!article.title) return <p>Article indisponible</p>;

  const ImageUrl = article.imageArticle ? `${article.imageArticle.url}` : "https://via.placeholder.com/800";
  const additionalImages = article.additionalImages ? article.additionalImages.map(image => `${image.url}`) : [];

  // Divide images into columns for masonry layout
  const columns = 4;
  const masonryColumns = Array.from({ length: columns }, () => []);

  additionalImages.forEach((url, index) => {
    masonryColumns[index % columns].push(url);
  });

  return (
    <div className="mx-auto px-4 sm:px-6 lg:px-8 py-16 bg-cream">
      {/* Article Title */}
      <motion.h1
        className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl xl:text-9xl font-light leading-4 font-cormorant mb-8 sm:mb-10 md:mb-12 lg:mb-14 mt-5 text-[#333333]"
        initial={{ opacity: 0, y: -30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        {article.title}
      </motion.h1>

      {/* Article Subtitle */}
      <p className="text-lg sm:text-xl text-gray-700 mb-4">{article.subtitle}</p>

      {/* Metadata */}
      <div className="text-xs sm:text-sm text-gray-600 mb-10">
        <span>Categorie: {article.category}</span> |
        <span> Reading Time: {article.readingTime} min</span> |
        <span> Auteur: {article.author}</span>
      </div>

      {/* Article Image */}
      <div className="mb-10">
        <img 
          src={ImageUrl || "https://via.placeholder.com/800"} 
          alt={article.title} 
          className="w-full h-auto max-h-96 rounded-lg shadow-lg object-cover"
          />
      </div>

      {/* Article Content */}
      <div className="prose prose-lg text-[#333333] mb-16">
        {article.content ? article.content.map((block, index) => (
          <p key={index} className="mb-6">
            {block.children.map((child, childIndex) => (
              <span key={childIndex}>{child.text}</span>
            ))}
          </p>
        )) : null}
      </div>

      {/* Masonry Layout for Additional Images */}
      <section className="mb-16">
        <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-9xl font-light font-cormorant text-[#333333] mb-8">Additional Images</h2>
        {/* SI additionalImage is not null */}
        {additionalImages.length ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
            {masonryColumns.map((column, columnIndex) => (
              <div key={columnIndex} className="flex flex-col gap-4">
                {column.map((url, index) => (
                  <img key={index} src={url} alt={`Additional Image ${index + 1}`} className="w-full h-auto rounded-lg shadow-lg object-cover" />
                ))}
              </div>
            ))}
          </div>
        ) : (
          <p className="text-lg text-gray-700">No additional images available.</p>
        )}
      </section>

      {/* Read more articles */}
      <section className="mt-16">
        <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-9xl font-light font-cormorant text-[#333333] mb-8">Read More</h2>
        <div className="grid grid-cols-1 sm:grid-cols-1 md:grid-cols-2 gap-8">
          {articlesAdditionals
            .filter(additionalArticle => additionalArticle.id !== article.id)
            .slice(0, 2)
            .map((additionalArticle) => (
              <div key={additionalArticle.id} className="bg-cream rounded-lg shadow-lg overflow-hidden">
                <img
                  src={additionalArticle.imageArticle ? `${additionalArticle.imageArticle.url}` : "https://via.placeholder.com/800"}
                  alt={additionalArticle.title}
                  className="w-full h-48 sm:h-64 md:h-80 lg:h-96 object-cover"
                />
                <div className="p-6">
                  <h3 className="text-lg sm:text-xl font-bold mb-2">{additionalArticle.title}</h3>
                  <p className="text-gray-700 mb-4">{additionalArticle.subtitle}</p>
                  <a href={`/categories/mobilier/blog/${additionalArticle.documentId}`} className="text-blue-500 hover:underline">Read More</a>
                </div>
              </div>
            ))}
        </div>
      </section>
    </div>
  );
};

export default ArticlePage;